// The one place a route is authorised. A pathless layout route between /:role
// (which renders AppShell) and every leaf under it, so the rail and the footer
// still render while this owns the workspace, and no page file learns about
// roles.
// per docs/shape-ops-route-authorisation.md §Placement
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useTranslate } from '@tolgee/react';
import { MudInfoBox } from '@egov-moldova/mud-react';
import { useNavContext } from '../../hooks/use-nav-context';
import { useSession } from '../../hooks/use-session';
import { decideRoute, signInPath } from './route-authorisation';

type DeniedState = { deniedFrom?: string } | null;

export function RouteGuard(): JSX.Element {
  const { t } = useTranslate();
  const location = useLocation();
  const { data: session, status } = useSession();
  const nav = useNavContext();

  // An identity that is not established yet is not a permission, so nothing
  // authenticated renders and nothing behind it fetches. Nor is a Registrar
  // kind the bff is still being asked about: refusing on it would send the
  // reader home from an address they may open.
  if (status === 'pending' || nav.pending) {
    return <p>{t('common.loading')}</p>;
  }

  // A 401 arrives as `data: null` and is handled below. Reaching here means the
  // session could not be read at all — a 5xx, a dead network — which says
  // nothing about who the reader is. Fail closed, but do not evict them: a
  // reload is what fixes this and they need to be told so.
  if (status === 'error') {
    return (
      <MudInfoBox emphasis="strong" role="alert" variant="error">
        {t('route_guard.session_unavailable')}
      </MudInfoBox>
    );
  }

  const decision = decideRoute(location.pathname, session ?? null, nav.context);

  if (decision.verdict === 'sign-in') {
    return <Navigate to={signInPath()} replace />;
  }

  if (decision.verdict === 'redirect') {
    return <Navigate to={decision.to} replace state={{ deniedFrom: location.pathname }} />;
  }

  // Constitution IV: a redirect the reader did not ask for must say so. The
  // sentence belongs here rather than on a page, because the guard owns both the
  // refusal and the explanation for it.
  const deniedFrom = (location.state as DeniedState)?.deniedFrom;

  return (
    <>
      {deniedFrom ? (
        <MudInfoBox emphasis="strong" role="alert" variant="warning">
          {t('route_guard.denied')}
        </MudInfoBox>
      ) : null}
      <Outlet />
    </>
  );
}
